<template>
    <div>
        <div class="karte">
            <Breadcrumb />
            <FilterButton :query="query" @reload="queryChanged" />

            <!-- Left Sidebar -->
            <div class="left-sidebar hide-on-mobile">
                <div class="panel-header ml50">
                    <div class="page-title inblock">
                        <h5 class="inblock">Kultur in Kiel</h5>
                    </div>

                    <button @click="showFilter = !showFilter"
                        class="btn btn-fill icon-after float-right btn-focus-design"
                        aria-label="Weitere Filter anzeigen">
                        Filter
                        <i class="material-icons" aria-hidden="true">{{ !showFilter ? 'keyboard_arrow_down' :
                            'keyboard_arrow_up' }}</i>
                    </button>
                </div>

                <div class="filter-section mt20" v-if="showFilter">
                    <form action="">
                        <select name="" class="mb10" v-model="query.selectedCategory" data-placeholder="Kategorien"
                            @change="applyChangesToQuery">
                            <option v-for="cat in categories" :value="cat.value" :key="cat.value">{{ cat.label }}
                            </option>
                        </select>
                        <select name="" class="mb10" data-placeholder="Tags" @change="applyChangesToQuery"
                            v-model="query.selectedTag">
                            <option v-for="tag in tags" :key="tag.value" :value="tag.value">{{ tag.label }}</option>
                        </select>
                        <button type="button" @click="resetAllFilters" class="mt10"
                            style="background: #fff; color: #000; cursor: pointer;"
                            v-if="query.selectedCategory || query.selectedTag || query.selectedKeyword">
                            Alle Filter zurücksetzen
                        </button>
                    </form>
                </div>

                <div class="row card-sections" v-if="dataSet.meta != null" tabindex="-1">
                    <div v-for="(content, $index) in dataSet.data" :key="content.id" :data-num="$index + 1"
                        :data-content-id="content.id">
                        <CardInstitution :class="isLastOfDataSet($index)" :id="'vertical-content-' + content.id"
                            v-if="content.content_type_name == 'institution'" :content="content" :marginRight="'0px;'"
                            :disableLink="true" @highlight="highlightThisContent" />
                        <CardAttraction :class="isLastOfDataSet($index)" :id="'vertical-content-' + content.id"
                            v-if="content.content_type_name == 'sehenswrdigkeit'" :marginRight="'0px;'"
                            :content="content" @highlight="highlightThisContent" :disableLink="true" />
                    </div>
                    <infinite-loading :identifier="'desktopInfiniteScroller'" @infinite="loadNext">
                        <div slot="no-more"></div>
                        <div slot="no-results"></div>
                    </infinite-loading>
                </div>

            </div>

            <!-- Map Section -->
            <div class="map-section" :class="active === SECTIONS.KARTE ? 'map-section-map' : 'map-section-list'">
                <multiple-map ref="theMap" v-if="active === SECTIONS.KARTE && dataSet.data != null"
                    :contents="dataSet.data" @highlight="highlightThisContent"
                    @remember-last-visited="rememberLastVisited"></multiple-map>

                <div class="only-on-mobile">
                    <div class="btn-custom btn-list-map-switch">
                        <button :class="['btn btn-custom__item', active === SECTIONS.KARTE ? 'active' : '']"
                            @click="active = SECTIONS.KARTE">Karte</button>
                        <button :class="['btn btn-custom__item', active === SECTIONS.LISTE ? 'active' : '']"
                            @click="active = SECTIONS.LISTE">Liste</button>
                    </div>

                    <div v-if="active === SECTIONS.KARTE" class="scroll-container horizontal-scroll">
                        <div v-for="(content, $index) in dataSet.data" :key="$index" :data-num="$index + 1"
                            @click="rememberLastVisited(content.id)" :data-content-id="content.id">
                            <CardInstitution :location="'mapview'" :id="'horizontal-content-' + content.id"
                                v-if="content.content_type_name == 'institution'" :content="content" :isSmall="true"
                                :marginRight="'0px;'" :disableLink="true" @highlight="highlightThisContent"
                                style="margin-bottom:0px;" />
                            <CardAttraction :location="'mapview'" :id="'horizontal-content-' + content.id"
                                v-if="content.content_type_name == 'sehenswrdigkeit'" :content="content"
                                @highlight="highlightThisContent" :isSmall="true" :marginRight="'0px;'" />
                        </div>
                        <infinite-loading :identifier="'mobileInfiniteScroller'" @infinite="loadNext">
                            <div slot="no-more"></div>
                            <div slot="no-results"></div>
                        </infinite-loading>
                    </div>

                    <div v-if="active === SECTIONS.LISTE" class="list-container">
                        <div v-for="(content, $index) in dataSet.data" :key="$index" :data-num="$index + 1"
                            @click="rememberLastVisited(content.id)" :data-content-id="content.id">
                            <CardInstitution v-if="content.content_type_name == 'institution'" :content="content"
                                :marginRight="'0px;'" />
                            <CardAttraction v-if="content.content_type_name == 'sehenswrdigkeit'" :content="content"
                                :marginRight="'0px;'" />
                        </div>
                        <infinite-loading :identifier="'mobileInfiniteScrollerVertical'" @infinite="loadNext">
                            <div slot="no-more"></div>
                            <div slot="no-results"></div>
                        </infinite-loading>
                    </div>

                </div>
            </div>
        </div>


        <div class="footer-margin hide-on-mobile">
        </div>

    </div>
</template>

<script>
import Breadcrumb from '@/components/controls/Breadcrumb.vue';
import CardInstitution from '@/components/cards/CardInstitution.vue';
import CardAttraction from '@/components/cards/CardAttraction.vue';
import FilterButton from '@/components/controls/FilterButton.vue';
import MultipleMap from '@/components/map/multiple.vue';
import InfiniteLoading from 'vue-infinite-loading';
import VueScrollTo from 'vue-scrollto';
import { isMobile } from 'mobile-device-detect';
import { screenSizeMixin } from '@/mixins/screenSizeMixin';
import { showLoader, hideLoader } from '@/utils/helpers';
import { getMapContents } from '@/api/content';
import { buildFilterSignatureFromQuery, saveLastVisitedItem, loadLastVisitedItem } from '@/utils/helpers';

const SECTIONS = Object.freeze({
    KARTE: 'KARTE',
    LISTE: 'LISTE'
});

export default {
    name: 'MapView',
    mixins: [screenSizeMixin],
    components: {
        MultipleMap,
        CardAttraction,
        CardInstitution,
        Breadcrumb,
        FilterButton,
        InfiniteLoading,
    },
    data() {
        return {
            loader: null,
            isCurrentlyLoading: false,
            showFilter: false,
            SECTIONS,
            isItMobile: isMobile ? true : false,
            active: SECTIONS.KARTE,
            dataSet: {
                total: 0,
                data: null,
                meta: null,
                query: {
                    page: 1,
                    limit: 250,
                    keyword: '',
                    type: 'teaser',
                    view_status: 1,
                    onlyWithLocation: true,
                    resourceType: 'teaser',
                    selectedAppId: this.$appId,
                    selectedContentTypeId: this.$institutionId,
                    selectedCategory: null,
                    selectedTag: null,
                },
            },
            scrollVerticalOptions: {
                container: '.card-sections',
                easing: 'ease-in',
                lazy: false,
                offset: -60,
                force: true,
                cancelable: true,
                x: false,
                y: true,
            },
            scrollHorizontalOptions: {
                container: '.horizontal-scroll',
                easing: 'ease-in',
                lazy: false,
                offset: -60,
                force: true,
                cancelable: true,
                x: true,
                y: false,
            },
            query: {
                selectedTag: null,
                selectedKeyword: null,
                selectedCategory: null,
            },
            attractionCategoryCardContent: {
                label: "KulTour-Stationen",
                value: "Sehenswürdigkeiten"
            },
        }
    },
    created() {
        this.initializeFromUrl();
    },
    mounted() {
        // Try to scroll after component is mounted
        this.attemptScrollToLastVisited();
    },
    watch: {
        $screenWidth(newWidth) {
            if (newWidth >= 991) {
                this.active = SECTIONS.KARTE;
            }
        },
    },
    computed: {
        categories() {
            var categories = JSON.parse(JSON.stringify(this.$store.getters.getCategories));
            /*removes the last element*/
            categories.pop();
            categories.push(this.attractionCategoryCardContent);
            return categories;
        },
        tags() {
            return this.$store.getters.getTags;
        },
    },
    methods: {
        showLoader,
        hideLoader,
        isLastOfDataSet(index) {
            var length = this.dataSet.data.length - 1;
            if (length == index) {
                return "last-elem";
            }
            return "";
        },
        async getContents() {
            this.loader = this.showLoader(this.loader);
            const { limit, page } = this.dataSet.query;
            const { data, meta } = await getMapContents(this.dataSet.query);
            if (this.dataSet.data != null) {
                this.dataSet.data = this.dataSet.data.concat(data);
            }
            else {
                this.dataSet.data = data;
            }
            data.forEach((element, index) => {
                element['index'] = (page - 1) * limit + index + 1;
            });
            this.dataSet.meta = meta;
            this.dataSet.total = meta.total;
            this.loader = this.hideLoader(this.loader);

            // Try to scroll to last visited element if available
            this.attemptScrollToLastVisited();
        },
        loadNext($state) {
            /*try to load the next page*/
            if (this.dataSet.meta.current_page == this.dataSet.meta.last_page) {
                /*do nothing we're already at the last page*/
                $state.complete();
            }
            else {
                this.isCurrentlyLoading = true;
                this.dataSet.query.page = this.dataSet.meta.current_page + 1;
                this.getContents()
                    .then(function () {
                        $state.loaded();
                        // Try to scroll after each page load
                        this.attemptScrollToLastVisited();
                    })
                    .finally(() => {
                        //$state.complete(); 
                        this.isCurrentlyLoading = false;
                    });
            }
        },
        applyChangesToQuery() {
            this.dataSet.query.page = 1;
            this.updateUrl();
            this.queryChanged(this.query);
        },
        queryChanged(query) {
            // Store individual values for consistency
            this.dataSet.query.selectedCategory = query.selectedCategory || null;
            this.dataSet.query.selectedTag = query.selectedTag || null;

            // Update the main query with new values
            this.query.selectedCategory = query.selectedCategory || null;
            this.query.selectedTag = query.selectedTag || null;
            this.query.selectedKeyword = query.selectedKeyword || null;

            // ORIGINAL backend logic - UNCHANGED
            this.dataSet.query.keyword = [];
            this.dataSet.query.subCategory = null;

            // Only process actual filter values, not metadata properties
            Object.keys(query).forEach((k) => {
                // Skip our metadata properties
                if (k === 'selectedCategory' || k === 'selectedTag' || k === 'selectedKeyword') {
                    return;
                }

                if (Array.isArray(query[k]) && query[k].length > 0) {
                    var array = query[k];
                    //console.log(array);
                    Object.keys(array).forEach((l) => {
                        if (array[l] != null) {
                            this.dataSet.query.keyword.push(array[l]);
                        }
                    });
                }
                else if (!Array.isArray(query[k]) && query[k] != null) {
                    if (query[k] === "Sehenswürdigkeiten") {
                        this.dataSet.query.subCategory = query[k];
                    }
                    else {
                        this.dataSet.query.subCategory = "Institutions";
                        this.dataSet.query.keyword.push(query[k]);
                    }
                }
            });

            // Add the selected values to the backend query
            if (query.selectedCategory && query.selectedCategory !== null) {
                if (query.selectedCategory === "Sehenswürdigkeiten") {
                    this.dataSet.query.subCategory = query.selectedCategory;
                } else {
                    this.dataSet.query.subCategory = "Institutions";
                    this.dataSet.query.keyword.push(query.selectedCategory);
                }
            }

            if (query.selectedTag && query.selectedTag !== null) {
                this.dataSet.query.keyword.push(query.selectedTag);
            }

            if (query.selectedKeyword && query.selectedKeyword !== null) {
                this.dataSet.query.keyword.push(query.selectedKeyword);
            }

            this.dataSet.query.page = 1;
            this.dataSet.data = null;
            this.getContents();
            this.updateUrl();
        },
        updateUrl() {
            const queryParams = {};

            // Add category filter
            if (this.query.selectedCategory && this.query.selectedCategory !== null) {
                queryParams.category = encodeURIComponent(this.query.selectedCategory);
            }

            // Add tag filter
            if (this.query.selectedTag && this.query.selectedTag !== null) {
                queryParams.tag = encodeURIComponent(this.query.selectedTag);
            }

            // Add keyword filter
            if (this.query.selectedKeyword && this.query.selectedKeyword !== null) {
                queryParams.keyword = encodeURIComponent(this.query.selectedKeyword);
            }

            // Add page if not first page
            if (this.dataSet.query.page > 1) {
                queryParams.page = this.dataSet.query.page;
            }

            this.$router.replace({
                query: {
                    ...queryParams
                }
            });
        },
        initializeFromUrl() {
            const query = this.$route.query;

            // Initialize category from URL
            if (query.category) {
                this.query.selectedCategory = decodeURIComponent(query.category);
                // Show filters if there are any in the URL
                this.showFilter = true;
            }

            // Initialize tag from URL
            if (query.tag) {
                this.query.selectedTag = decodeURIComponent(query.tag);
                this.showFilter = true;
            }

            // Initialize keyword from URL
            if (query.keyword) {
                this.query.selectedKeyword = decodeURIComponent(query.keyword);
                this.showFilter = true;
            }

            // Initialize page from URL
            if (query.page) {
                const page = parseInt(query.page) || 1;
                this.dataSet.query.page = page;
            }

            // Apply filters and fetch data
            if (query.category || query.tag || query.keyword) {
                this.applyChangesToQuery();
            } else {
                this.getContents();
            }
        },
        resetAllFilters() {
            this.query.selectedCategory = null;
            this.query.selectedTag = null;
            this.query.selectedKeyword = null;
            this.dataSet.query.selectedCategory = null;
            this.dataSet.query.selectedTag = null;
            this.dataSet.query.page = 1;

            // Clear URL parameters
            this.$router.replace({
                query: {}
            });

            this.queryChanged(this.query);
        },
        highlightThisContent(params) {
            Object.keys(this.dataSet.data).forEach((k) => {
                this.dataSet.data[k].highlight = false;
                if (this.dataSet.data[k].id == params.content.id) {
                    this.dataSet.data[k].highlight = true;

                    if (params.sender != "map") {
                        /*open the popup*/
                        this.$refs.theMap.openPopup(params.content);
                        //console.log(this.$refs.theMap);
                    }
                    else {
                        if (this.isItMobile) {
                            VueScrollTo.scrollTo(('#horizontal-content-' + this.dataSet.data[k].id), '2000', this.scrollHorizontalOptions);
                        }
                        else {
                            VueScrollTo.scrollTo(('#vertical-content-' + this.dataSet.data[k].id), '2000', this.scrollVerticalOptions);
                        }
                    }
                }
            });
        },
        getCurrentFilterSignature() {
            const q = this.$route.query || {};
            const qp = {};
            if (q.category) qp.category = decodeURIComponent(q.category);
            if (q.tag) qp.tag = decodeURIComponent(q.tag);
            if (q.keyword) qp.keyword = decodeURIComponent(q.keyword);
            if (q.page) qp.page = q.page;
            return buildFilterSignatureFromQuery(qp);
        },
        rememberLastVisited(id) {
            try {
                const signature = this.getCurrentFilterSignature();
                saveLastVisitedItem(this.$route.path, signature, id);
            } catch (e) {
                console.error('Error remembering last visited:', e);
            }
        },
        attemptScrollToLastVisited() {
            this.$nextTick(() => {
                try {
                    const signature = this.getCurrentFilterSignature();
                    const lastId = loadLastVisitedItem(this.$route.path, signature);
                    if (lastId && typeof lastId === 'number') {
                        let attempts = 0;
                        const maxAttempts = 50;
                        const tryScroll = () => {
                            attempts++;

                            if (this.isItMobile) {
                                // Mobile: try horizontal scroll first (map mode), then list mode
                                if (this.active === SECTIONS.KARTE) {
                                    const target = '#horizontal-content-' + lastId;
                                    const elem = document.querySelector(target);
                                    if (elem) {
                                        VueScrollTo.scrollTo(target, '800', this.scrollHorizontalOptions);
                                        return;
                                    }
                                } else if (this.active === SECTIONS.LISTE) {
                                    const target = `[data-content-id="${lastId}"]`;
                                    const elem = document.querySelector(target);
                                    if (elem) {
                                        elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        return;
                                    }
                                }
                            } else {
                                const verticalId = `vertical-content-${lastId}`;
                                const horizontalId = `horizontal-content-${lastId}`;
                                const dataSelector = `[data-content-id="${lastId}"]`;

                                const verticalElem = document.getElementById(verticalId);
                                const horizontalElem = document.getElementById(horizontalId);
                                const dataElem = document.querySelector(dataSelector);

                                const elem = verticalElem || horizontalElem || dataElem;
                                if (elem) {
                                    elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    return;
                                }
                            }

                            // If element not found and we haven't exceeded max attempts, try again
                            if (attempts < maxAttempts) {
                                setTimeout(tryScroll, 200);
                            }
                        };
                        tryScroll();
                    }
                } catch (e) {
                    console.error('Scroll error:', e);
                }
            });
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/_variables.scss';

.scroll-pagination-btn {
    width: 100%;
    padding: 8px;
    text-align: center;
    color: $primary;
    border: 1px solid $primary;
    cursor: pointer;
}

.scroll-pagination-btn:hover {
    color: #fff;
    background-color: $primary;
}

.scroll-pagination-top {
    margin-top: 20px;
}

.scroll-pagination-bottom {
    margin-bottom: 20px;
}

.horizontal-scroll {
    width: 100vw;
}

.only-on-mobile {
    display: none;

    @media(max-width: 991px) {
        display: block;
    }
}

.karte {
    position: relative;
    padding-top: 70px;
    display: flex;
    height: 100vh;
    margin-bottom: 146px;
    overflow: hidden;
    background: transparent;

    @media(max-width: 991px) {
        height: calc(100vh - 40px);
    }

    .left-sidebar {
        flex: 0 450px;
        background: #fff;
        flex-direction: column;
        padding: 35px 20px 0 40px;

        &.hide-on-mobile {
            display: flex;

            @media(max-width: 991px) {
                display: none;
            }
        }


        .panel-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .card-sections {
            display: block;
            padding-left: 15px;
            margin-right: 0px;
            height: 100%;
            max-height: calc(100vh - 150px);
            overflow: hidden;
            overflow-y: auto;
            margin-top: 20px;

            .last-elem {
                margin-bottom: 200px !important;
            }

            &::-webkit-scrollbar-track {
                padding: 2px;
                margin-bottom: 20px;
                background-color: rgba($color: #c4c4c4, $alpha: 0.4)
            }

            &::-webkit-scrollbar {
                width: 10px;
                padding: 2px;
            }

            &::-webkit-scrollbar-thumb {
                border-radius: 10px;
                // box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.8);
                box-shadow: inset 0 0 6px rgba($color: #a4a4a4, $alpha: 0.8);
                background-color: rgba($color: #c4c4c4, $alpha: 0.8);
                border: 2px solid rgba($color: #c4c4c4, $alpha: 0.4);
            }
        }
    }

    .map-section {
        flex: 1;
        background: #fff;
        overflow: auto;

        &.map-section-list {
            height: 100vh;
        }

        img {
            position: absolute;
            height: 100%;
            min-width: 100%;
        }

        .scroll-container {
            z-index: 1001;
            position: fixed;
            top: 110px;

            @media (max-width: 991px) {
                top: auto;
                bottom: 50px;
            }
        }

        .list-container {
            position: relative;
            top: 50px;
            max-width: 500px;
            margin: 0 auto;
            margin-bottom: 200px;
            padding: 0 15px;
        }

        .btn-custom {
            position: relative;
            display: block;
            width: 200px;
            margin: 0 auto;
            top: 22px;
            border-radius: 30px;
            box-shadow: -5px 5px 15px 0px rgba($color: $primary, $alpha: 0.2);

            &.btn-list-map-switch {
                z-index: 1001;
                position: absolute;
                top: 110px;
                left: 50%;
                transform: translate(-50%, -50%);
            }

            &__item {
                padding: 10px 25px;
                border: 0;
                width: 100px;
                background: #fff;
                font-size: 16px;
                font-family: 'elliot-regular';
                font-weight: 400;
                text-transform: capitalize;
                color: $darkGray;
                border-radius: 0;

                &:first-child {
                    border-top-left-radius: 30px;
                    border-bottom-left-radius: 30px;
                }

                &:last-child {
                    border-top-right-radius: 30px;
                    border-bottom-right-radius: 30px;
                }

                &.active {
                    background: $infoColor;
                    font-weight: 700;
                    color: #fff;
                }
            }
        }
    }

    @media (max-width: 991px) {
        margin-bottom: 0;
        height: 100vh;
    }

    @media (max-width: 350px) {
        padding-top: 60px;
    }
}
</style>
